import { has, isEqual } from 'lodash';
import { assocPath, pipe } from 'lodash/fp';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { FiltersContext } from '../contexts';
import { FiltersStore, getFilterStore } from '../store';
import {
  DefaultFilters,
  FilterData,
  MergeFilterValueConfig,
  OnChangeFilter,
  OnChangeFilters,
  OnChangeMultipleFilter,
  RegisterFunction,
} from '../types/filter.types';

export type FiltersContextProviderProps<T> = {
  readonly onChange: OnChangeFilters<T>;
  readonly children: ReactNode;
  readonly value: FilterData<T>;
  readonly store?: FiltersStore;
  readonly cacheKey?: string;
};

export function FiltersContextProvider<T extends DefaultFilters = DefaultFilters>({
  onChange: onChangeFromProps,
  value,
  children,
  cacheKey,
}: FiltersContextProviderProps<T>) {
  const store = useRef(cacheKey ? getFilterStore(cacheKey) : null).current;
  const [initFilters, setInitFilters] = useState<FilterData<T>>({} as FilterData<T>);

  const onChange: OnChangeFilter<T> = useCallback(
    (path, { value: newValue, isMultiple }) => {
      if (typeof path === 'string') {
        const tResult = pipe(assocPath(`${path}.value`, newValue), assocPath(`${path}.isMultiple`, isMultiple))(value);
        onChangeFromProps(tResult);
      }
    },
    [value, onChangeFromProps],
  );

  const onChangeMultiple: OnChangeMultipleFilter<T> = useCallback(
    (filters) => {
      const tResult = filters.reduce((acc, { path, value }) => {
        if (typeof path === 'string') {
          return pipe(assocPath(`${path}.value`, value), assocPath(`${path}.isMultiple`, true))(acc);
        }
        return acc;
      }, value);
      onChangeFromProps(tResult);
    },
    [onChangeFromProps, value],
  );

  const onResetFilters = useCallback(() => {
    onChangeFromProps(initFilters);
  }, [initFilters, onChangeFromProps]);

  const onApplyFilters = useCallback(
    (filters: FilterData<T>) => {
      onChangeFromProps(filters);
    },
    [onChangeFromProps],
  );

  const onResetSpecificFilter = useCallback((pickedFilter: keyof FilterData<T>, filters: FilterData<T>) => {
    if (Array.isArray(filters[pickedFilter].value)) {
      onChangeFromProps({
        ...filters,
        [pickedFilter]: {
          ...filters[pickedFilter],
          value: [],
        },
      });
    }
    if (typeof filters[pickedFilter].value === 'string') {
      onChangeFromProps({
        ...filters,
        [pickedFilter]: {
          ...filters[pickedFilter],
          value: '',
        },
      });
    }
  }, []);

  const onRegister: RegisterFunction = useCallback(({ isMultiple = false, path, value }) => {
    setInitFilters((prev: FilterData<T>) => {
      if (has(prev, path)) {
        console.warn(`Filter with name ${path.toString()} already exists`);
        // TBD: what we should do when we re init some filter
        return prev;
      }
      const newValue = value ?? (isMultiple ? [] : '');
      return assocPath(path, { isMultiple, value: newValue }, prev);
    });
  }, []);

  const mergeFilterValueConfig: MergeFilterValueConfig<T> = useCallback(
    (filtersConfig, currentFilters) =>
      Object.keys(filtersConfig).reduce((acc, key) => {
        const config = filtersConfig[key as keyof T];
        const currentFilter = currentFilters?.[key as keyof T];
        const value = currentFilter?.value ?? config.value;
        return {
          ...acc,
          [key]: {
            ...config,
            value,
          },
        };
      }, {} as FilterData<T>),
    [],
  );

  const filters = mergeFilterValueConfig(initFilters, value);

  const hasFiltersSelected = !isEqual(initFilters, filters);

  return (
    <FiltersContext.Provider
      value={{
        onChange,
        onRegister,
        onResetFilters,
        onResetSpecificFilter: onResetSpecificFilter as any,
        onChangeMultiple,
        onApplyFilters: onApplyFilters as any,
        filters,
        store,
        hasFiltersSelected,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
}
